// Settings page component for InfluenceHub MVP
// Account-level settings for influencer accounts
// Handles: Account info display, password reset, public profile visibility

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { useCurrentInfluencerProfile } from '../hooks/useCurrentInfluencerProfile';
import { sendPasswordResetEmail, updateProfile, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig.js';
import { isUsernameAvailable, createOrUpdateInfluencerProfile } from '../lib/models/influencer';
import { triggerProfilePropagation } from '../lib/profile/triggerProfilePropagation';
import DashboardShell from '../components/layout/DashboardShell';

const Settings = ({ onNavigate }) => {
  const { user } = useAuth();
  const { influencerProfile, loading: profileLoading } = useCurrentInfluencerProfile();
  
  // Account info state
  const [accountInfo, setAccountInfo] = useState({
    displayName: '',
    handle: '',
    email: '',
  });
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [passwordForReauth, setPasswordForReauth] = useState('');
  const [isReauthenticating, setIsReauthenticating] = useState(false);

  // Password reset state
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetSuccessMessage, setResetSuccessMessage] = useState('');
  const [resetErrorMessage, setResetErrorMessage] = useState('');

  // Public profile visibility state
  const [isSavingVisibility, setIsSavingVisibility] = useState(false);
  const [visibilityError, setVisibilityError] = useState('');

  // Initialize account info from profile
  useEffect(() => {
    if (influencerProfile) {
      setAccountInfo({
        displayName: influencerProfile?.displayName || influencerProfile?.identity?.displayName || '',
        handle: influencerProfile?.username || influencerProfile?.identity?.handle || '',
        email: user?.email || '',
      });
    }
  }, [influencerProfile, user]);

  const isPublic = influencerProfile?.isPublic ?? true; // Default to true if undefined

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!user?.email) {
      setResetErrorMessage('No email address found. Please contact support.');
      return;
    }

    setIsSendingReset(true);
    setResetErrorMessage('');
    setResetSuccessMessage('');

    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetSuccessMessage(`Password reset email sent to ${user.email}`);
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setResetErrorMessage('We couldn\'t send the email. Please try again.');
    } finally {
      setIsSendingReset(false);
    }
  };

  // Handle account info input changes
  const handleAccountInfoChange = (field, value) => {
    setAccountInfo((prev) => ({ ...prev, [field]: value }));
    setAccountError('');
    setAccountSuccess('');
  };

  // Handle saving account info
  const handleSaveAccountInfo = async () => {
    if (!user?.uid) return;

    setAccountError('');
    setAccountSuccess('');

    // Validation
    if (!accountInfo.displayName.trim()) {
      setAccountError('Name is required');
      return;
    }

    if (!accountInfo.handle.trim()) {
      setAccountError('Handle is required');
      return;
    }

    // Validate handle format
    const handleRegex = /^[a-z0-9_-]+$/;
    const normalizedHandle = accountInfo.handle.toLowerCase().trim();
    if (!handleRegex.test(normalizedHandle)) {
      setAccountError('Handle can only contain letters, numbers, dashes, and underscores');
      return;
    }

    if (normalizedHandle.length < 3) {
      setAccountError('Handle must be at least 3 characters');
      return;
    }

    if (normalizedHandle.length > 30) {
      setAccountError('Handle must be 30 characters or less');
      return;
    }

    // Check handle uniqueness if it changed
    const currentHandle = influencerProfile?.username || influencerProfile?.identity?.handle || '';
    if (normalizedHandle !== currentHandle.toLowerCase()) {
      setIsSavingAccount(true);
      try {
        const isAvailable = await isUsernameAvailable(normalizedHandle);
        if (!isAvailable) {
          setAccountError('That handle is already taken. Please choose another.');
          setIsSavingAccount(false);
          return;
        }
      } catch (err) {
        console.error('Error checking handle availability:', err);
        setAccountError('Could not verify handle availability. Please try again.');
        setIsSavingAccount(false);
        return;
      }
    }

    setIsSavingAccount(true);

    try {
      // Update Firebase Auth displayName
      if (user && accountInfo.displayName !== user.displayName) {
        await updateProfile(user, { displayName: accountInfo.displayName.trim() });
      }

      // Update Firestore influencer profile (name and handle only - email changes are handled in Security section)
      await createOrUpdateInfluencerProfile(user.uid, {
        displayName: accountInfo.displayName.trim(),
        username: normalizedHandle,
        contactEmail: user?.email || '', // Keep current email, don't change it here
        identity: {
          displayName: accountInfo.displayName.trim(),
          handle: normalizedHandle,
        },
      });
      const propagate = await triggerProfilePropagation({
        uid: user.uid,
        getToken: () => (auth.currentUser ? auth.currentUser.getIdToken() : Promise.resolve(null)),
      });
      if (!propagate.ok && propagate.error) {
        console.warn('[Settings] profile propagation failed', propagate.error);
      }

      setAccountSuccess('Account information updated successfully!');
      setIsEditingAccount(false);
      setShowPasswordPrompt(false);
      setTimeout(() => setAccountSuccess(''), 5000);
    } catch (err) {
      console.error('Error saving account info:', err);
      if (err.code === 'auth/email-already-in-use') {
        setAccountError('That email is already in use by another account.');
      } else if (err.code === 'auth/invalid-email') {
        setAccountError('Please enter a valid email address.');
      } else if (err.code === 'auth/operation-not-allowed') {
        setAccountError('Email verification is required. Please check your email settings in Firebase Console.');
      } else {
        setAccountError(err.message || 'Failed to save account information. Please try again.');
      }
    } finally {
      setIsSavingAccount(false);
    }
  };

  // Handle re-authentication and email update
  const handleReauthenticateAndSave = async () => {
    if (!user?.email || !passwordForReauth) {
      setAccountError('Password is required to change your email.');
      return;
    }

    setIsReauthenticating(true);
    setAccountError('');

    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, passwordForReauth);
      await reauthenticateWithCredential(user, credential);

      // Update Firebase Auth displayName
      if (accountInfo.displayName !== user.displayName) {
        await updateProfile(user, { displayName: accountInfo.displayName.trim() });
      }

      // Update Firestore influencer profile
      const normalizedHandle = accountInfo.handle.toLowerCase().trim();
      await createOrUpdateInfluencerProfile(user.uid, {
        displayName: accountInfo.displayName.trim(),
        username: normalizedHandle,
        contactEmail: user?.email || '',
        identity: {
          displayName: accountInfo.displayName.trim(),
          handle: normalizedHandle,
        },
      });
      const propagate = await triggerProfilePropagation({
        uid: user.uid,
        getToken: () => (auth.currentUser ? auth.currentUser.getIdToken() : Promise.resolve(null)),
      });
      if (!propagate.ok && propagate.error) {
        console.warn('[Settings] profile propagation failed', propagate.error);
      }

      setAccountSuccess('Account information updated successfully!');
      setIsEditingAccount(false);
      setShowPasswordPrompt(false);
      setPasswordForReauth('');
      setTimeout(() => setAccountSuccess(''), 5000);
    } catch (err) {
      console.error('Error re-authenticating:', err);
      if (err.code === 'auth/wrong-password') {
        setAccountError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/email-already-in-use') {
        setAccountError('That email is already in use by another account.');
      } else if (err.code === 'auth/invalid-email') {
        setAccountError('Please enter a valid email address.');
      } else {
        setAccountError(err.message || 'Failed to update email. Please try again.');
      }
    } finally {
      setIsReauthenticating(false);
    }
  };

  // Handle canceling account info edits
  const handleCancelAccount = () => {
    setAccountInfo({
      displayName: influencerProfile?.displayName || influencerProfile?.identity?.displayName || '',
      handle: influencerProfile?.username || influencerProfile?.identity?.handle || '',
      email: user?.email || '',
    });
    setIsEditingAccount(false);
    setAccountError('');
    setAccountSuccess('');
    setShowPasswordPrompt(false);
    setPasswordForReauth('');
  };

  // Handle public profile visibility toggle
  const handleToggleVisibility = async () => {
    if (!user?.uid) return;

    const newVisibility = !isPublic;
    setIsSavingVisibility(true);
    setVisibilityError('');

    try {
      await updateDoc(doc(db, 'influencers', user.uid), {
        isPublic: newVisibility,
      });
    } catch (err) {
      console.error('Error updating profile visibility:', err);
      setVisibilityError('Failed to update visibility. Please try again.');
    } finally {
      setIsSavingVisibility(false);
    }
  };

  // Loading state
  if (profileLoading) {
  return (
      <DashboardShell onNavigate={onNavigate}>
        <div className="p-6">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-sm text-gray-500">Loading settings...</p>
              </div>
            </div>
          </div>
        </div>
      </DashboardShell>
    );
  }

  // Error state
  if (!influencerProfile && !profileLoading) {
    return (
      <DashboardShell onNavigate={onNavigate}>
        <div className="p-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
              We couldn't load your settings right now. Please refresh the page.
            </div>
                    </div>
                  </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell onNavigate={onNavigate}>
      <div className="p-6">
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Page Header */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage your account settings and preferences.
            </p>
          </div>

          {/* Account Info Card */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Account</h2>
              {!isEditingAccount && (
                <button
                  type="button"
                  onClick={() => setIsEditingAccount(true)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  Edit
                </button>
                  )}
                </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={accountInfo.displayName}
                  onChange={(e) => handleAccountInfoChange('displayName', e.target.value)}
                  disabled={!isEditingAccount}
                  className={`block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-gray-900 ${
                    isEditingAccount 
                      ? 'bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500' 
                      : 'bg-slate-50 cursor-not-allowed'
                  }`}
                />
              </div>
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Handle
                  </label>
                <div className="flex rounded-md shadow-sm">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 bg-slate-50 px-3 text-gray-500 text-sm">
                    @
                  </span>
                  <input
                    type="text"
                    value={accountInfo.handle}
                    onChange={(e) => {
                      // Remove @ if user types it, and convert to lowercase
                      const value = e.target.value.replace(/^@/, '').toLowerCase();
                      handleAccountInfoChange('handle', value);
                    }}
                    disabled={!isEditingAccount}
                    pattern="[a-z0-9_-]+"
                    className={`block w-full min-w-0 flex-1 rounded-none rounded-r-md border border-slate-300 px-3 py-2 text-sm text-gray-900 ${
                      isEditingAccount 
                        ? 'bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500' 
                        : 'bg-slate-50 cursor-not-allowed'
                    }`}
                  />
                </div>
                {isEditingAccount && (
                  <p className="mt-1 text-xs text-gray-500">
                    Your profile will be available at {typeof window !== "undefined" ? window.location.host : "influencehub.net"}/u/{accountInfo.handle || 'username'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                  <input
                  type="email"
                  value={accountInfo.email}
                  readOnly
                  className="block w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-gray-900 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Your email address is managed through your account authentication.
                </p>
                </div>

              {/* Password prompt for email change */}
              {showPasswordPrompt && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4 space-y-3">
                  <p className="text-sm font-medium text-amber-900">
                    Confirm your password to change your email
                  </p>
                  <p className="text-xs text-amber-700">
                    For security, we need to verify your identity before changing your email address.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={passwordForReauth}
                      onChange={(e) => setPasswordForReauth(e.target.value)}
                      placeholder="Enter your current password"
                      className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordPrompt(false);
                        setPasswordForReauth('');
                        setAccountError('');
                      }}
                      disabled={isReauthenticating}
                      className="text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleReauthenticateAndSave}
                      disabled={isReauthenticating || !passwordForReauth}
                      className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isReauthenticating ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Verifying...
                        </>
                      ) : (
                        'Confirm & Save'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {accountError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {accountError}
                </div>
              )}

              {accountSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
                  {accountSuccess}
                </div>
              )}

              {isEditingAccount && (
                <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                    type="button"
                    onClick={handleCancelAccount}
                    disabled={isSavingAccount}
                    className="text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  <button
                    type="button"
                    onClick={handleSaveAccountInfo}
                    disabled={isSavingAccount}
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingAccount ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Save changes'
                    )}
                  </button>
                </div>
              )}
            </div>
                  </div>

          {/* Security Card */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Security</h2>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                You can change your password by sending a reset link to your email.
              </p>
              <button
                type="button"
                onClick={handlePasswordReset}
                disabled={isSendingReset || !user?.email}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSendingReset ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send password reset email'
                )}
              </button>
              
              {resetSuccessMessage && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
                  {resetSuccessMessage}
                  </div>
                )}

              {resetErrorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {resetErrorMessage}
                  </div>
                )}
              </div>
            </div>

          {/* Public Profile Visibility Card */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Media kit</h2>
              <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Control whether your media kit is visible to brands.
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Media kit visibility
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {isPublic 
                      ? 'Your media kit is visible to brands and can be found via your media kit link.'
                      : 'Your media kit is hidden and cannot be accessed via your media kit link.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleVisibility}
                  disabled={isSavingVisibility}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isPublic ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                  role="switch"
                  aria-checked={isPublic}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isPublic ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                </div>
              
              {visibilityError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {visibilityError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DashboardShell>
  );
};

export default Settings;
